<div class="bid-shell">
  <section class="hero-card">
    <div class="hero-copy">
      <div class="eyebrow">Bess</div>
      <h1>Place Bid</h1>
      <p>Suggest and place blockwise battery bids using forecast market prices. DAM is open for tomorrow. RTM is open from current block + 4.</p>
    </div>
    <div class="hero-icon">
      <lucide-icon [img]="BatteryChargingIcon" [size]="28"></lucide-icon>
    </div>
  </section>

  <section class="toolbar-card">
    <div class="toolbar-grid">
      <label>
        <span>Product</span>
        <select [ngModel]="product()" (ngModelChange)="setProduct($event)">
          <option value="DAM">DAM</option>
          <option value="RTM">RTM</option>
        </select>
      </label>

      <label>
        <span>Bid Date</span>
        <input type="date" [min]="minAllowedDate()" [ngModel]="selectedDate()" (ngModelChange)="setDate($event)" />
      </label>

      <label>
        <span>Plant</span>
        <input
          type="text"
          list="plant-search-options"
          placeholder="Search by plant, type, MW"
          [ngModel]="plantSearch()"
          (ngModelChange)="setPlantSearch($event)"
        />
        <datalist id="plant-search-options">
          <option *ngFor="let panel of plantPanels()" [value]="panel.asset.entry_id">{{ panel.asset.plant_type }}</option>
        </datalist>
      </label>

      <div class="toolbar-actions">
        <button type="button" class="secondary-btn" [disabled]="syncing()" (click)="refreshSuggestions()">
          <lucide-icon [img]="RefreshCcwIcon" [size]="16"></lucide-icon>
          <span>{{ syncing() ? 'Refreshing...' : 'Refresh Suggestions' }}</span>
        </button>
      </div>
    </div>

    <div class="rule-strip">
      <span><lucide-icon [img]="CalendarRangeIcon" [size]="15"></lucide-icon>{{ eligibleDateLabel() }}</span>
      <span><lucide-icon [img]="CheckCircle2Icon" [size]="15"></lucide-icon>Current block: {{ currentBlockLabel() }} ({{ currentBlock() }})</span>
      <span>Plants loaded: <strong>{{ assets().length }}</strong></span>
      <span *ngIf="plantSearch()">Matching plants: <strong>{{ filteredPlantPanels().length }}</strong></span>
    </div>
  </section>

  <section class="summary-grid">
    <article class="summary-card">
      <small>Eligible Blocks</small>
      <strong>{{ overallSummary().totalEligible }}</strong>
    </article>
    <article class="summary-card">
      <small>Charge Suggestions</small>
      <strong>{{ overallSummary().chargeCount }}</strong>
    </article>
    <article class="summary-card">
      <small>Discharge Suggestions</small>
      <strong>{{ overallSummary().dischargeCount }}</strong>
    </article>
    <article class="summary-card">
      <small>Selected Bid MW</small>
      <strong>{{ overallSummary().selectedMw | number:'1.2-2' }}</strong>
    </article>
  </section>

  <section class="bid-card">
    <div class="section-head">
      <div>
        <h2>Plantwise Bid Suggestions</h2>
        <p>All plants are listed first. Expand a plant accordion to review and place its blockwise bids.</p>
      </div>
    </div>

    <div class="empty-state" *ngIf="loading()">Loading bid suggestions...</div>
    <div class="empty-state" *ngIf="!loading() && plantPanels().length === 0">No plants available for bid suggestions.</div>
    <div class="empty-state" *ngIf="!loading() && plantPanels().length > 0 && filteredPlantPanels().length === 0">No plants match the current search.</div>

    <div class="accordion-list" *ngIf="!loading() && filteredPlantPanels().length > 0">
      <article class="plant-panel" *ngFor="let panel of filteredPlantPanels()" [class.plant-panel-open]="isExpanded(panel.asset.id)">
        <button type="button" class="plant-toggle" (click)="togglePanel(panel.asset.id)">
          <div class="plant-copy">
            <strong>{{ panel.asset.entry_id }}</strong>
            <span>{{ panel.asset.power_capacity_mw }} MW • {{ panel.asset.plant_type || 'Battery Asset' }}</span>
          </div>
          <div class="plant-kpis">
            <span>{{ panel.summary.chargeCount }} charge</span>
            <span>{{ panel.summary.dischargeCount }} discharge</span>
            <span>{{ panel.summary.selectedMw | number:'1.2-2' }} MW selected</span>
          </div>
        </button>

        <div class="plant-body" *ngIf="isExpanded(panel.asset.id)">
          <div class="head-actions">
            <button type="button" class="ghost-btn" [disabled]="panel.summary.totalEligible === 0" (click)="selectAllEligible(panel.asset.id, true)">Select All</button>
            <button type="button" class="ghost-btn" [disabled]="panel.summary.selectedCount === 0" (click)="selectAllEligible(panel.asset.id, false)">Clear</button>
            <button type="button" class="primary-btn inline-place-btn" [disabled]="placing() || panel.summary.selectedCount === 0" (click)="placeSelectedBids(panel.asset.id)">Place Selected Bids</button>
          </div>

          <div class="table-wrap" style="max-height: 500px; overflow-y: auto;">
            <table>
              <thead class="sticky-top">
                <tr>
                  <th>Select</th>
                  <th>Block</th>
                  <th>Time</th>
                  <th>Forecast Price</th>
                  <th>Suggested Action</th>
                  <th>Bid MW</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let item of panel.suggestions"
                  [class.row-ineligible]="!item.eligible"
                  [class.row-charge]="item.action === 'charge'"
                  [class.row-discharge]="item.action === 'discharge'"
                  [class.row-existing]="!!item.existingScheduleId"
                >
                  <td>
                    <input
                      type="checkbox"
                      [checked]="item.selected"
                      [disabled]="!item.eligible || item.action === 'hold' || !!item.existingScheduleId"
                      (ngModelChange)="toggleSuggestion(panel.asset.id, item.block, $event)"
                      [ngModel]="item.selected"
                    />
                  </td>
                  <td>B{{ item.block }}</td>
                  <td>{{ item.blockLabel }}</td>
                  <td>{{ item.marketPrice | number:'1.2-2' }}</td>
                  <td>
                    <span class="action-pill" [class.charge-pill]="item.action === 'charge'" [class.discharge-pill]="item.action === 'discharge'" [class.hold-pill]="item.action === 'hold'">
                      {{ item.action | titlecase }}
                    </span>
                  </td>
                  <td>{{ item.bidMw | number:'1.2-2' }}</td>
                  <td>{{ rowState(item) }}</td>
                  <td>{{ item.reason }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </article>
    </div>
  </section>
</div>
